import React, { useMemo } from "react";
import CustomTimePicker from "./CustomTimePicker";

const WEEKDAYS = [
  { value: 0, label: "Domingo" },
  { value: 1, label: "Segunda-feira" },
  { value: 2, label: "Terça-feira" },
  { value: 3, label: "Quarta-feira" },
  { value: 4, label: "Quinta-feira" },
  { value: 5, label: "Sexta-feira" },
  { value: 6, label: "Sábado" },
];

function getId(item) {
  return item?.id ?? item?._id;
}

function safeNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function normalizeMedicationType(value, flask = {}) {
  const raw = String(value || "").trim().toUpperCase();
  const nameLower = String(flask?.name || flask?.medicationName || "").toLowerCase();

  if (
    raw === "PO" ||
    raw === "PÓ" ||
    nameLower.includes("glutamina") ||
    nameLower.includes("creatina") ||
    nameLower.includes("whey") ||
    nameLower.includes("colageno")
  ) {
    return "PO";
  }

  return raw;
}

function isUnitMedicationType(type) {
  return type === "COMPRIMIDO" || type === "CAPSULA" || type === "PO";
}

function getUnitFieldLabel(type) {
  if (type === "CAPSULA") return "Quantas cápsulas por dose?";
  if (type === "PO") return "Quantos dosadores / scoops por tomada?";
  return "Quantos comprimidos por dose?";
}

function getUnitFieldPlaceholder(type) {
  if (type === "CAPSULA") return "Ex: 1 cápsula";
  if (type === "PO") return "Ex: 1 dose ou 2 scoops";
  return "Ex: 1 comprimido";
}

function getTypeIcon(type) {
  if (type === "INJETAVEL") return "💉";
  if (type === "CAPSULA") return "💊";
  if (type === "COMPRIMIDO") return "💊";
  if (type === "PO") return "🥤";
  return "💊";
}

function getDateLabel(type) {
  if (type === "INJETAVEL") return "Data da 1ª aplicação";
  if (type === "PO") return "Data do 1º consumo";
  return "Data da 1ª dose";
}

function getTimeLabel(type) {
  if (type === "INJETAVEL") return "Horário da 1ª aplicação";
  if (type === "PO") return "Horário do 1º consumo";
  return "Horário da 1ª dose";
}

function FlaskOptions({ flasks, concLabelForFlask }) {
  return (
    <>
      <option value="">Selecione o medicamento em estoque…</option>

      {flasks.map((flask) => {
        const flaskId = String(getId(flask));
        const concentrationLabel = concLabelForFlask(flask);
        const type = normalizeMedicationType(flask?.medicationType, flask);
        const icon = getTypeIcon(type);

        return (
          <option key={flaskId} value={flaskId}>
            {icon} {flask?.name || "Medicamento"}
            {flask?.brand ? ` • ${flask.brand}` : ""} ({concentrationLabel})
          </option>
        );
      })}
    </>
  );
}

export default function DosePlannerForm({
  mode,
  setMode,
  selectableFlasks = [],
  concLabelForFlask,
  singleForm,
  setSingleForm,
  cycleForm,
  setCycleForm,
  onSubmitSingle,
  onSubmitCycle,
  writeLastDoseTime,
  todayInputDate,
}) {
  const safeSingleForm = {
    flaskId: "",
    dateLocal: "",
    timeLocal: "",
    dosageMg: "",
    notificationActive: true,
    ...(singleForm || {}),
  };

  const safeCycleForm = {
    flaskId: "",
    startDateLocal: todayInputDate(),
    startTimeLocal: "19:00",
    dosageMg: "",
    frequencyType: "DAILY",
    everyNDays: 2,
    everyNHours: 8,
    weeklyDay: 1,
    durationDays: 28,
    notificationActive: true,
    ...(cycleForm || {}),
  };

  const selectedSingleFlask = useMemo(() => {
    return (
      selectableFlasks.find(
        (flask) => String(getId(flask)) === String(safeSingleForm.flaskId)
      ) || null
    );
  }, [selectableFlasks, safeSingleForm.flaskId]);

  const singleMedicationType = normalizeMedicationType(
    selectedSingleFlask?.medicationType,
    selectedSingleFlask
  );
  const singleIsUnitMedication = isUnitMedicationType(singleMedicationType);
  const singleMgPerUnit = safeNumber(selectedSingleFlask?.mgPerUnit);

  const singleDisplayedDoseValue = useMemo(() => {
    const dosageMg = safeNumber(safeSingleForm.dosageMg);

    if (dosageMg == null) return "";

    if (
      singleIsUnitMedication &&
      singleMgPerUnit != null &&
      singleMgPerUnit > 0
    ) {
      return String(dosageMg / singleMgPerUnit);
    }

    return String(safeSingleForm.dosageMg || "");
  }, [
    safeSingleForm.dosageMg,
    singleIsUnitMedication,
    singleMgPerUnit,
  ]);

  const selectedCycleFlask = useMemo(() => {
    return (
      selectableFlasks.find(
        (flask) => String(getId(flask)) === String(safeCycleForm.flaskId)
      ) || null
    );
  }, [selectableFlasks, safeCycleForm.flaskId]);

  const cycleMedicationType = normalizeMedicationType(
    selectedCycleFlask?.medicationType,
    selectedCycleFlask
  );
  const cycleIsUnitMedication = isUnitMedicationType(cycleMedicationType);
  const cycleMgPerUnit = safeNumber(selectedCycleFlask?.mgPerUnit);

  const cycleDisplayedDoseValue = useMemo(() => {
    const dosageMg = safeNumber(safeCycleForm.dosageMg);

    if (dosageMg == null) return "";

    if (cycleIsUnitMedication && cycleMgPerUnit != null && cycleMgPerUnit > 0) {
      return String(dosageMg / cycleMgPerUnit);
    }

    return String(safeCycleForm.dosageMg || "");
  }, [safeCycleForm.dosageMg, cycleIsUnitMedication, cycleMgPerUnit]);

  // Resumo amigável das doses que serão geradas
  const cycleSummaryText = useMemo(() => {
    const dur = Number(safeCycleForm.durationDays) || 1;
    const freq = safeCycleForm.frequencyType;

    let count = 0;
    let desc = "";

    if (freq === "DAILY") {
      count = dur;
      desc = "todos os dias";
    } else if (freq === "EVERY_N_HOURS") {
      const stepH = Math.max(1, Number(safeCycleForm.everyNHours) || 8);
      count = Math.ceil((dur * 24) / stepH);
      desc = `a cada ${stepH} horas`;
    } else if (freq === "EVERY_N_DAYS") {
      const step = Math.max(1, Number(safeCycleForm.everyNDays) || 1);
      count = Math.ceil(dur / step);
      desc = step === 2 ? "dia sim, dia não" : `a cada ${step} dias`;
    } else if (freq === "WEEKLY") {
      count = Math.ceil(dur / 7);
      const dayObj = WEEKDAYS.find((w) => w.value === Number(safeCycleForm.weeklyDay));
      desc = `toda ${dayObj?.label || "semana"}`;
    }

    return `Serão criadas ${count} dose(s) programadas (${desc}) durante ${dur} dias.`;
  }, [safeCycleForm.durationDays, safeCycleForm.frequencyType, safeCycleForm.everyNDays, safeCycleForm.everyNHours, safeCycleForm.weeklyDay]);

  return (
    <section className="liti-card p-5 shadow-md border border-[#ECE7FA] space-y-4 bg-white rounded-[28px]">
      {/* SELETOR DE MODALIDADE (PILL TABS LITI) */}
      <div className="flex p-1 rounded-full bg-[#ECE7FA]/60 border border-[#ECE7FA]">
        <button
          type="button"
          onClick={() => setMode("SINGLE")}
          className={`flex-1 py-2.5 rounded-full text-xs font-bold transition-all duration-200 cursor-pointer ${
            mode === "SINGLE"
              ? "bg-[#6C54D8] text-white shadow-xs"
              : "text-[#6D6680] hover:text-[#6C54D8]"
          }`}
        >
          Dose Única
        </button>

        <button
          type="button"
          onClick={() => setMode("CYCLE")}
          className={`flex-1 py-2.5 rounded-full text-xs font-bold transition-all duration-200 cursor-pointer ${
            mode === "CYCLE"
              ? "bg-[#6C54D8] text-white shadow-xs"
              : "text-[#6D6680] hover:text-[#6C54D8]"
          }`}
        >
          Repetir Doses
        </button>
      </div>

      {mode === "SINGLE" ? (
        <>
          <div className="border-b border-[#ECE7FA] pb-2">
            <h2 className="text-base font-bold text-[#24152F] font-serif">
              Agendar Dose Pontual
            </h2>
            <p className="text-xs text-[#6D6680] font-medium mt-0.5">
              Defina um horário específico para tomar seu medicamento.
            </p>
          </div>

          <form onSubmit={onSubmitSingle} className="space-y-4">
            {/* 1. SELEÇÃO DA MEDICAÇÃO */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1">
                Qual medicamento?
              </label>
              <select
                className="input-base h-11 w-full text-xs font-bold text-[#24152F] cursor-pointer"
                value={safeSingleForm.flaskId}
                onChange={(e) =>
                  setSingleForm((prev) => ({
                    ...prev,
                    flaskId: e.target.value,
                    dosageMg: "",
                  }))
                }
                required
              >
                <FlaskOptions
                  flasks={selectableFlasks}
                  concLabelForFlask={concLabelForFlask}
                />
              </select>
            </div>

            {/* 2. DOSAGEM */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1">
                {singleIsUnitMedication
                  ? getUnitFieldLabel(singleMedicationType)
                  : "Quanto você vai usar por dose? (mg)"}
              </label>

              <input
                type="number"
                step="0.01"
                min="0.01"
                className="input-base h-11 w-full text-xs font-medium"
                placeholder={
                  singleIsUnitMedication
                    ? getUnitFieldPlaceholder(singleMedicationType)
                    : "Ex: 0.5 ou 15"
                }
                value={singleDisplayedDoseValue}
                onChange={(e) => {
                  const rawValue = e.target.value;

                  if (rawValue === "") {
                    setSingleForm((prev) => ({
                      ...prev,
                      dosageMg: "",
                    }));
                    return;
                  }

                  const numericValue = Number(rawValue);

                  if (!Number.isFinite(numericValue)) return;

                  if (
                    singleIsUnitMedication &&
                    singleMgPerUnit != null &&
                    singleMgPerUnit > 0
                  ) {
                    setSingleForm((prev) => ({
                      ...prev,
                      dosageMg: String(numericValue * singleMgPerUnit),
                    }));
                    return;
                  }

                  setSingleForm((prev) => ({
                    ...prev,
                    dosageMg: rawValue,
                  }));
                }}
                required
              />

              {selectedSingleFlask && (
                <p className="text-[11px] font-bold text-[#6C54D8] mt-1">
                  ✨ Dosagem registrada: {concLabelForFlask(selectedSingleFlask)}
                </p>
              )}
            </div>

            {/* 3. DATA E HORÁRIO */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1.5">
                  {getDateLabel(singleMedicationType)}
                </label>
                <input
                  type="date"
                  className="input-base h-16 w-full text-xs font-bold"
                  value={safeSingleForm.dateLocal}
                  onChange={(e) =>
                    setSingleForm((prev) => ({
                      ...prev,
                      dateLocal: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <CustomTimePicker
                label={getTimeLabel(singleMedicationType)}
                value={safeSingleForm.timeLocal}
                onChange={(time) => {
                  setSingleForm((prev) => ({
                    ...prev,
                    timeLocal: time,
                  }));
                  writeLastDoseTime(time);
                }}
              />
            </div>

            <button
              type="submit"
              className="btn-primary h-11 w-full text-xs font-bold uppercase tracking-wider shadow-md cursor-pointer mt-2"
            >
              Agendar Dose
            </button>
          </form>
        </>
      ) : (
        <>
          <div className="border-b border-[#ECE7FA] pb-2">
            <h2 className="text-base font-bold text-[#24152F] font-serif">
              Criar Rotina de Repetição de Doses
            </h2>
            <p className="text-xs text-[#6D6680] font-medium mt-0.5">
              Programe lembretes recorrentes para o seu tratamento.
            </p>
          </div>

          <form onSubmit={onSubmitCycle} className="space-y-4">
            {/* 1. MEDICAMENTO */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1">
                Qual medicamento?
              </label>

              <select
                className="input-base h-11 w-full text-xs font-bold text-[#24152F] cursor-pointer"
                value={safeCycleForm.flaskId}
                onChange={(e) =>
                  setCycleForm((prev) => ({
                    ...prev,
                    flaskId: e.target.value,
                    dosageMg: "",
                  }))
                }
                required
              >
                <FlaskOptions
                  flasks={selectableFlasks}
                  concLabelForFlask={concLabelForFlask}
                />
              </select>
            </div>

            {/* 2. DOSAGEM POR APLICAÇÃO */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1">
                {cycleIsUnitMedication
                  ? getUnitFieldLabel(cycleMedicationType)
                  : "Quanto você vai usar por dose? (mg)"}
              </label>

              <input
                type="number"
                step="0.01"
                min="0.01"
                className="input-base h-11 w-full text-xs font-medium"
                placeholder={
                  cycleIsUnitMedication
                    ? getUnitFieldPlaceholder(cycleMedicationType)
                    : "Ex: 0.5 ou 15"
                }
                value={cycleDisplayedDoseValue}
                onChange={(e) => {
                  const rawValue = e.target.value;

                  if (rawValue === "") {
                    setCycleForm((prev) => ({
                      ...prev,
                      dosageMg: "",
                    }));
                    return;
                  }

                  const numericValue = Number(rawValue);

                  if (!Number.isFinite(numericValue)) return;

                  if (
                    cycleIsUnitMedication &&
                    cycleMgPerUnit != null &&
                    cycleMgPerUnit > 0
                  ) {
                    setCycleForm((prev) => ({
                      ...prev,
                      dosageMg: String(numericValue * cycleMgPerUnit),
                    }));
                    return;
                  }

                  setCycleForm((prev) => ({
                    ...prev,
                    dosageMg: rawValue,
                  }));
                }}
                required
              />

              {selectedCycleFlask && (
                <p className="text-[11px] font-bold text-[#6C54D8] mt-1">
                  ✨ Dosagem registrada: {concLabelForFlask(selectedCycleFlask)}
                </p>
              )}
            </div>

            {/* 3. DATA E HORÁRIO DA 1ª DOSE */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1.5">
                  {getDateLabel(cycleMedicationType)}
                </label>
                <input
                  type="date"
                  className="input-base h-16 w-full text-xs font-bold"
                  value={safeCycleForm.startDateLocal}
                  onChange={(e) =>
                    setCycleForm((prev) => ({
                      ...prev,
                      startDateLocal: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <CustomTimePicker
                label={getTimeLabel(cycleMedicationType)}
                value={safeCycleForm.startTimeLocal}
                onChange={(time) => {
                  setCycleForm((prev) => ({
                    ...prev,
                    startTimeLocal: time,
                  }));
                  writeLastDoseTime(time);
                }}
              />
            </div>

            {/* 4. SELEÇÃO DA FREQUÊNCIA (COM OPÇÃO A CADA X HORAS) */}
            <div className="space-y-2 pt-2 border-t border-[#ECE7FA]">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680]">
                Frequência de repetição
              </label>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setCycleForm((prev) => ({
                      ...prev,
                      frequencyType: "DAILY",
                    }))
                  }
                  className={`py-2.5 px-2 rounded-2xl text-[11px] font-bold transition-all border cursor-pointer text-center ${
                    safeCycleForm.frequencyType === "DAILY"
                      ? "bg-[#6C54D8] text-white border-[#6C54D8] shadow-xs"
                      : "bg-white text-[#24152F] border-[#ECE7FA] hover:bg-[#ECE7FA]/60"
                  }`}
                >
                  Diariamente
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setCycleForm((prev) => ({
                      ...prev,
                      frequencyType: "EVERY_N_HOURS",
                      everyNHours: prev.everyNHours || 8,
                    }))
                  }
                  className={`py-2.5 px-2 rounded-2xl text-[11px] font-bold transition-all border cursor-pointer text-center ${
                    safeCycleForm.frequencyType === "EVERY_N_HOURS"
                      ? "bg-[#6C54D8] text-white border-[#6C54D8] shadow-xs"
                      : "bg-white text-[#24152F] border-[#ECE7FA] hover:bg-[#ECE7FA]/60"
                  }`}
                >
                  A cada X horas
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setCycleForm((prev) => ({
                      ...prev,
                      frequencyType: "EVERY_N_DAYS",
                      everyNDays: prev.everyNDays || 2,
                    }))
                  }
                  className={`py-2.5 px-2 rounded-2xl text-[11px] font-bold transition-all border cursor-pointer text-center ${
                    safeCycleForm.frequencyType === "EVERY_N_DAYS"
                      ? "bg-[#6C54D8] text-white border-[#6C54D8] shadow-xs"
                      : "bg-white text-[#24152F] border-[#ECE7FA] hover:bg-[#ECE7FA]/60"
                  }`}
                >
                  A cada N dias
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setCycleForm((prev) => ({
                      ...prev,
                      frequencyType: "WEEKLY",
                    }))
                  }
                  className={`py-2.5 px-2 rounded-2xl text-[11px] font-bold transition-all border cursor-pointer text-center ${
                    safeCycleForm.frequencyType === "WEEKLY"
                      ? "bg-[#6C54D8] text-white border-[#6C54D8] shadow-xs"
                      : "bg-white text-[#24152F] border-[#ECE7FA] hover:bg-[#ECE7FA]/60"
                  }`}
                >
                  Semanalmente
                </button>
              </div>

              {/* OPÇÃO A CADA X HORAS */}
              {safeCycleForm.frequencyType === "EVERY_N_HOURS" && (
                <div className="p-3.5 rounded-2xl bg-[#ECE7FA]/50 border border-[#ECE7FA] mt-2">
                  <label className="block text-xs font-bold text-[#24152F] mb-1">
                    Repetir a cada quantas horas?
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    className="input-base h-10 w-full text-xs font-bold bg-white"
                    placeholder="Ex: 8 (para 3x ao dia) ou 12 (para 2x ao dia)"
                    value={safeCycleForm.everyNHours}
                    onChange={(e) =>
                      setCycleForm((prev) => ({
                        ...prev,
                        everyNHours: Math.max(1, Number(e.target.value) || 1),
                      }))
                    }
                  />
                  <p className="text-[11px] text-[#6C54D8] font-bold mt-1">
                    ✨ Dose programada a cada {safeCycleForm.everyNHours} horas ({Math.round(24 / (Number(safeCycleForm.everyNHours) || 8))}x ao dia).
                  </p>
                </div>
              )}

              {/* OPÇÃO A CADA N DIAS */}
              {safeCycleForm.frequencyType === "EVERY_N_DAYS" && (
                <div className="p-3.5 rounded-2xl bg-[#ECE7FA]/50 border border-[#ECE7FA] mt-2">
                  <label className="block text-xs font-bold text-[#24152F] mb-1">
                    Repetir a cada quantos dias?
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="input-base h-10 w-full text-xs font-bold bg-white"
                    placeholder="Ex: 2 (para dia sim / dia não) ou 7"
                    value={safeCycleForm.everyNDays}
                    onChange={(e) =>
                      setCycleForm((prev) => ({
                        ...prev,
                        everyNDays: Math.max(1, Number(e.target.value) || 1),
                      }))
                    }
                  />
                  <p className="text-[11px] text-[#6C54D8] font-bold mt-1">
                    {Number(safeCycleForm.everyNDays) === 2
                      ? "✨ Configurado para: Dia sim, Dia não."
                      : `✨ Dose programada a cada ${safeCycleForm.everyNDays} dias.`}
                  </p>
                </div>
              )}

              {safeCycleForm.frequencyType === "WEEKLY" && (
                <div className="p-3.5 rounded-2xl bg-[#ECE7FA]/50 border border-[#ECE7FA] mt-2">
                  <label className="block text-xs font-bold text-[#24152F] mb-1">
                    Qual dia da semana?
                  </label>
                  <select
                    className="input-base h-10 w-full text-xs font-bold bg-white cursor-pointer"
                    value={safeCycleForm.weeklyDay}
                    onChange={(e) =>
                      setCycleForm((prev) => ({
                        ...prev,
                        weeklyDay: Number(e.target.value),
                      }))
                    }
                  >
                    {WEEKDAYS.map((w) => (
                      <option key={w.value} value={w.value}>
                        {w.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* 5. DURAÇÃO DA ROTINA */}
            <div className="pt-2 border-t border-[#ECE7FA]">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#6D6680] mb-1">
                Duração total da rotina
              </label>

              <select
                className="input-base h-11 w-full text-xs font-bold text-[#24152F] cursor-pointer"
                value={safeCycleForm.durationDays}
                onChange={(e) =>
                  setCycleForm((prev) => ({
                    ...prev,
                    durationDays: Number(e.target.value),
                  }))
                }
              >
                <option value={14}>14 dias (2 semanas)</option>
                <option value={28}>28 dias (4 semanas / 1 mês)</option>
                <option value={60}>60 dias (2 meses)</option>
                <option value={90}>90 dias (3 meses)</option>
                <option value={180}>180 dias (6 meses)</option>
              </select>
            </div>

            {/* RESUMO DAS DOSES */}
            <div className="rounded-2xl border border-[#CBF1DB] bg-[#CBF1DB]/50 p-3.5 text-xs font-bold text-emerald-950 text-center shadow-xs">
              ✨ {cycleSummaryText}
            </div>

            <button
              type="submit"
              className="btn-primary h-11 w-full text-xs font-bold uppercase tracking-wider shadow-md cursor-pointer"
            >
              Criar Rotina de Doses
            </button>
          </form>
        </>
      )}
    </section>
  );
}